import Router from 'next/router';
import { DEFAULT_FILTERS_PAGE_URL, PUBLIC_URL_PATH_LIST, TENANT_ID_NOT_REQUIRED_PATH_LIST } from '../constants/appUrls';
import { roles } from '../model/types';
import { pipe } from './helpers';

export const isEmailVerificationPage: (url: string) => boolean = (url) => url.startsWith('/email-verification/');
export const isRegisterPage: (url: string) => boolean = (url) => url.startsWith('/register');
export const isNewTicketPage: (url: string) => boolean = (url) => url.startsWith('/tickets/new');
export const isTicketsPage: (url: string) => boolean = (url) => url.startsWith('/tickets');
export const isCustomerPage: (url: string) => boolean = (url) => url.startsWith('/users/');
export const isOrganizationPage: (url: string) => boolean = (url) => url.startsWith('/organizations/');
export const isPagePluginPage: (url: string) => boolean = (url) => url.startsWith('/plugins/');
export const isAdminTicketFieldsPage: (url: string) => boolean = (url) => url.startsWith('/admin/ticket-fields');
export const isAdminUserFieldsPage: (url: string) => boolean = (url) => url.startsWith('/admin/user-fields');
export const isAdminOrganizationFieldsPage: (url: string) => boolean = (url) =>
    url.startsWith('/admin/organization-fields');
export const isAdminOptionSortPage: (url: string) => boolean = (url) => url.endsWith('/sort');

export const isInternalLink = (url: string): boolean =>
    url.includes(process.env.NEXT_PUBLIC_HOSTNAME_WITHOUT_SUBDOMAIN);

export const getUsersPageUserRedirectUrlString: (userId: string | number, role: roles | string) => string = (
    userId,
    role
) => {
    if (!userId) return DEFAULT_FILTERS_PAGE_URL;
    if (!role) return `/users/${userId}/tickets/requested?page=1&size=5`;
    if (role === 'ROLE_END_USER') return `/users/${userId}/tickets/requested?page=1&size=5`;
    return `/users/${userId}/tickets/assigned?page=1&size=5`;
};
export const getTicketsPageUserRedirectUrlString: (ticketKey: string | number, role: roles) => string = (
    ticketKey,
    role
) => {
    if (!ticketKey) return DEFAULT_FILTERS_PAGE_URL;
    if (!role) return `/tickets/${ticketKey}/requester/tickets/requested?page=1&size=5`;
    if (role === 'ROLE_END_USER') return `/tickets/${ticketKey}/requester/tickets/requested?page=1&size=5`;
    return `/tickets/${ticketKey}/requester/tickets/assigned?page=1&size=5`;
};

export const pushTo = (url: string) => Router.push(url);
export const redirectTo = (url: string) => Router.replace(url);

function isPublicPage(url: string): boolean {
    if (!url) return false;
    return PUBLIC_URL_PATH_LIST.some((urlPath) => url.startsWith(urlPath));
}
function isTenantIdRequiredPage(url: string): boolean {
    if (!url) return false;
    return !TENANT_ID_NOT_REQUIRED_PATH_LIST.some((urlPath) => url === urlPath);
}
const updateHash = (newHash: string) => {
    if (typeof window !== 'undefined') {
        window.location.hash = newHash;
    }
};
const compareHashTag = (tag: string) => {
    if (typeof window === 'undefined') return false;
    const fullHash = window.location.hash;
    if (!fullHash || !tag) return false;
    const hashTag = fullHash.split('=')[0].split('#')[1];
    return hashTag === tag;
};
const readHash = () => {
    if (typeof window === 'undefined') return null;
    return window.location.hash || null;
};
const getHashJSON = (tag: string) => {
    if (typeof window === 'undefined') return null;
    const hashJson = window.location.hash?.split(tag)?.[1]?.toString()?.substring(1);
    try {
        const parsedJson = JSON.parse(decodeURIComponent(hashJson));
        return parsedJson;
    } catch (error) {
        updateHash('');
        return null;
    }
};
const clearHash =
    () =>
    (url: string): string => {
        if (url?.includes('#')) url = url.split('#')[0];
        if (url?.includes('?')) url = url.split('?')[0];
        return url;
    };

const getTicketKeyFromUrl = (url: string): string => url.split('/').find((query) => query.includes('TICKET-'));
const getNewTicketKeyFromUrl = (url: string): string => `newTicket${url.split('/').pop()}`;
const getUserIdFromUrl = (url: string): string => url.split('/')[2];
const getOrganizationIdFromUrl = (url: string): string => url.split('/')[2];
const getPluginIdFromUrl = (url: string): string => url.split('/').pop();

export const getSafeTicketKeyFromUrl = (url: string): string => pipe(getTicketKeyFromUrl, clearHash())(url);
export const getSafeNewTicketKeyFromUrl = (url: string): string => pipe(getNewTicketKeyFromUrl, clearHash())(url);
export const getSafeUserIdFromUrl = (url: string): string => pipe(getUserIdFromUrl, clearHash())(url);
export const getSafeOrganizationIdFromUrl = (url: string): string => pipe(getOrganizationIdFromUrl, clearHash())(url);
export const getSafePluginIdFromUrl = (url: string): string => pipe(getPluginIdFromUrl, clearHash())(url);

export const getSelectedTabKeyFromUrl = (url: string): string => {
    if (isNewTicketPage(url)) return getSafeNewTicketKeyFromUrl(url);
    if (isTicketsPage(url)) return getSafeTicketKeyFromUrl(url);
    if (isCustomerPage(url)) return `USER-${getSafeUserIdFromUrl(url)}`;
    if (isOrganizationPage(url)) return `ORGANIZATION-${getOrganizationIdFromUrl(url)}`;
    if (isPagePluginPage(url)) return `PLUGIN-${getSafePluginIdFromUrl(url)}`;

    return null;
};
export { compareHashTag, getHashJSON, isPublicPage, isTenantIdRequiredPage, readHash, updateHash };
